<script lang="ts" setup>
import { computed, ref } from 'vue';

import { vAnchor } from '@dh-io-owpi/shared/src/directives/anchor.directive';
import type { ValidationNotification } from '@dh-io-owpi/shared/src/components/ErrorList/ValidationNotification';
import type { StageCtasMapped } from '@dh-io-owpi/shared-dynamic-stage/src/composables/useStageCtas';
import type { AemData } from '@dh-io-owpi/shared/src/plugins/aemData';
import type { Paint, StageVehicle, Style } from './lib/api';
import type { SwitcherKind } from './lib/types/switchers';
import { useLoading } from '@dh-io-owpi/shared/src/composables/useLoading';
import { usePurchasePriceTooltip } from '@dh-io-owpi/shared-dynamic-stage/src/composables/usePurchasePriceTooltip';
import { isCampaignModel } from '@dh-io-owpi/shared-dynamic-stage/src/utils/campaigns';
import { STAGE_ERROR_MESSAGE } from '@dh-io-owpi/shared-dynamic-stage/src/utils/constants';
import type { BrandId } from '@dh-io-owpi/backend-api/src/data-contracts';

// components
import { ErrorList } from '@dh-io-owpi/shared/src/components/ErrorList';
import StageBg from './components/StageBg/StageBg.vue';
import ThreesixtyViewer from './components/ThreesixtyViewer/ThreesixtyViewer.vue';
import SalesBar from './components/SalesBar/SalesBar.vue';
import ColorSwitcher from './components/ColorSwitcher/ColorSwitcher.vue';
import StyleSwitcher from './components/StyleSwitcher/StyleSwitcher.vue';
import PriceTag from './components/PriceTag/PriceTag.vue';
import CtaButtons from './components/CtaButtons/CtaButtons.vue';

const props = defineProps<{
  aemData: AemData;
  ctas: StageCtasMapped;
  errors: (ValidationNotification | string)[];
  purchasePriceTooltip?: string;
  stageVehicle?: StageVehicle;
  stageStyles?: Style[];
  selectedStyle?: Style;
  stagePaints?: Paint[];
  selectedPaint?: Paint;
  brandId: BrandId;
}>();

const emit = defineEmits<{
  (event: 'switchPaint', color: Paint): void;
  (event: 'switchStyle', style: Style): void;
}>();

const switchStyle = (style: Style) => emit('switchStyle', style);
const switchPaint = (paint: Paint) => emit('switchPaint', paint);

const { isLoading } = useLoading('stage');

const hasErrors = computed(() => props.errors.length > 0);

const expandedSwitcher = ref<SwitcherKind>('style');
const expandSwitcher = (kind: SwitcherKind) => (expandedSwitcher.value = kind);
const purchasePriceTooltip = usePurchasePriceTooltip(props.purchasePriceTooltip);

const isCampaignCandidate = computed(() => isCampaignModel(props.aemData.modelSeries, props.brandId));
const isDark = computed(() => props.brandId === 'MERCEDES_AMG');

// Don't render the background if we are in CSR/SPA mode and the stage vehicle is a campaign candidate
const renderBg = computed(() => props.aemData.renderMode !== 'csr' || props.stageVehicle || !isCampaignCandidate.value);
</script>

<template>
  <div
    v-anchor="'stage'"
    :class="['owpi-dynamic-stage', { 'owpi-dynamic-stage--dark': isDark }]"
    :style="aemData.renderMode === 'ssr' ? { display: 'none' } : undefined"
  >
    <slot />
    <StageBg
      v-if="renderBg"
      :subBrand="brandId"
      :campaignBg="stageVehicle?.backgroundImageUrl"
      class="owpi-dynamic-stage__bg"
    />
    <ErrorList v-if="aemData.isAemEditMode && hasErrors" class="owpi-dynamic-stage__error-list" :errors="errors" />
    <div
      v-if="!aemData.isAemEditMode && !stageVehicle && !isLoading && hasErrors"
      class="owpi-dynamic-stage__error wb-type-copy"
    >
      {{ $t('dynamicStage.errorMessage') ?? STAGE_ERROR_MESSAGE }}
    </div>

    <ThreesixtyViewer
      class="owpi-dynamic-stage__360-viewer"
      :stageVehicle="stageVehicle"
      :darkMode="isDark"
      :hasErrors="hasErrors"
    />

    <div class="owpi-dynamic-stage__heading">
      <h1 v-if="stageVehicle" class="wb-type-heading-xl">
        {{ stageVehicle?.modelName ?? '' }}
      </h1>
      <wb-skeleton v-else class="wb-type-heading-xl" :theme="isDark ? 'dark' : 'light'">&nbsp;</wb-skeleton>
      <div class="owpi-dynamic-stage__price">
        <label class="owpi-dynamic-stage__price-title wb-type-label">
          {{ $t('dynamicStage.purchasePricePrefix') || '...' }}
        </label>
        <PriceTag
          class="owpi-dynamic-stage__price-tag"
          :stageVehicle="stageVehicle"
          :isDark="isDark"
          :purchasePriceTooltip="purchasePriceTooltip"
          :position="'bottom'"
        />
      </div>
    </div>

    <StyleSwitcher
      v-if="stageStyles && stageStyles.length > 1"
      class="owpi-dynamic-stage__switchers owpi-dynamic-stage__switchers--style"
      :isDark="isDark"
      :styles="stageStyles.slice(0, 5)"
      :selectedStyle="selectedStyle"
      :isExpanded="expandedSwitcher === 'style'"
      @switch="switchStyle"
      @expand="expandSwitcher"
    />
    <div v-else class="owpi-dynamic-stage__switchers owpi-dynamic-stage__switchers--style" />
    <ColorSwitcher
      class="owpi-dynamic-stage__switchers owpi-dynamic-stage__switchers--color"
      :colors="stagePaints ?? []"
      :selectedColor="selectedPaint"
      :isExpanded="expandedSwitcher === 'paint'"
      :isDark="isDark"
      @switch="switchPaint"
      @expand="expandSwitcher"
    />

    <SalesBar
      class="owpi-dynamic-stage__sales-bar"
      :stageVehicle="stageVehicle"
      :aemData="aemData"
      :ctas="ctas"
      :purchasePriceTooltip="purchasePriceTooltip"
      :isDark="isDark"
      :brandId="brandId"
    />
    <CtaButtons class="owpi-dynamic-stage__ctas" :isDark="isDark" :ctas="ctas" trackingAction="dynamic_stage" />
  </div>
</template>

<style lang="scss">
@import './variables.sass';

.owpi-dynamic-stage {
  --stage-bg-color: var(--wb-white);
  --stage-fg-color: var(--wb-black);

  &--dark {
    --stage-bg-color: var(--wb-grey-15);
    --stage-fg-color: var(--wb-white);
  }

  background-color: var(--stage-bg-color);
  overflow: hidden;
  position: relative;
  display: grid !important; // needs to be !important to override the display: none from the SSR, added to avoid FOUC
  grid-auto-flow: column;
  > * {
    grid-column: 1;
  }

  &__error,
  &__error-list {
    z-index: 2;
    justify-self: center;
    color: var(--stage-fg-color);
    max-width: var(--wb-grid-width);
  }
  &__error {
    align-self: center;
  }

  &__switchers {
    &--style {
      position: relative;
    }

    &--color {
      justify-self: end;
      margin-inline-end: calc((100vw - var(--wb-grid-width)) / 2);
    }
  }
  &__360-viewer {
    @include wb-content-width;
  }
  // small version layout (mobile)
  @include breakpoint-to(mq3) {
    &__bg {
      grid-row: 1 / 5;
    }
    &__heading {
      @include wb-content-width;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      position: relative;
      text-align: center;
      grid-row: 1;
      margin-block: $mobile-heading-mt $mobile-heading-mb;
      height: $mobile-heading-height;
      color: var(--stage-fg-color);
      gap: $mobile-heading-gap;
      wb-skeleton {
        min-width: 200px;
      }

      h1 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }
    &__price {
      display: flex;
      flex-wrap: wrap;
      column-gap: var(--wb-spacing-xxs);
      justify-content: center;

      label {
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }
    &__360-viewer {
      grid-row: 2;
      margin-top: $mobile-mq2-car360-mt;
    }
    &__error,
    &__error-list {
      grid-row: 2 / 3;
    }
    &__switchers--color {
      // hide color switcher on mobile
      display: none !important;
    }
    &__switchers--style {
      @include wb-content-width;
      grid-row: 3;
      margin-block: 0 $mobile-style-switcher-mb;
      height: $mobile-style-switcher-height;
    }
    &__ctas {
      @include wb-content-width;
      grid-row: 4;
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-bottom: $ctas-mb;
      min-height: $ctas-height;
    }
  }

  @include breakpoint-between(mq3, mq4) {
    &__360-viewer {
      margin-top: $mobile-mq3-car360-mt;
      margin-bottom: $mobile-mq3-car360-mb;
    }
    &__heading {
      height: $mobile-mq3-heading-height;
      justify-content: end;

      h1 {
        -webkit-line-clamp: 1;
      }
    }
  }

  // big version layout (tablet/desktop)
  @include breakpoint-from(mq4) {
    &__ctas {
      display: none;
    }
    &__bg {
      grid-row: 1 / 4;
    }

    &__360-viewer {
      grid-row: 1;
      margin-top: $desktop-mq3-car360-mt;
      margin-bottom: $desktop-mq3-car360-mb;
      max-width: $desktop-mq3-car360-w;
    }

    &__error,
    &__error-list {
      grid-row: 1 / 2;
    }
    &__heading {
      // hide heading on desktop
      display: none;
    }

    &__switchers {
      grid-row: 2;
      margin-block: 0 $desktop-switchers-mb;
    }

    &__sales-bar {
      grid-row: 3;
    }
  }
}
</style>
